import * as React from 'react';
import { ForwardedRef, useImperativeHandle, useRef } from 'react';
import * as styles from './styles.css';
import cn from 'clsx';
import { ArrowDown12, CrossSmall24 } from '../../../Icons';

export interface IInputProps extends React.ComponentPropsWithoutRef<'input'> {
  ref?: ForwardedRef<HTMLInputElement> | null;
  placeholder: string;
  isDropdownOpen: boolean;
  handleOpenDropdown: (value: boolean) => void;
  handleReset: () => void;
  canResetFilter?: boolean;
}

export const Select: React.FC<IInputProps> = React.forwardRef((props, ref: ForwardedRef<HTMLInputElement>) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { isDropdownOpen, placeholder, canResetFilter, handleReset, handleOpenDropdown, ...inputProps } = props;

  const handleRemove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();

    inputRef.current?.blur();
    handleReset();
  };

  const handleInputClick = () => {
    if (isDropdownOpen) {
      handleOpenDropdown(false);
      inputRef.current?.blur();

      return;
    }

    handleOpenDropdown(true);
    inputRef.current?.focus();
  };

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  return (
    <div className={cn(styles['container'], { [styles['selected-options']]: canResetFilter })}>
      <input
        {...inputProps}
        ref={inputRef}
        className={cn(styles['input'])}
        placeholder={placeholder}
        onClick={handleInputClick}
      />
      <div className={styles['after-icon']} onClick={handleInputClick}>
        {canResetFilter && !isDropdownOpen ? (
          <div className={styles['cross']} onClick={handleRemove}>
            <CrossSmall24 color="currentColor" />
          </div>
        ) : (
          <div className={cn(styles['arrow'], { [styles['rotated']]: isDropdownOpen })}>
            <ArrowDown12 color={isDropdownOpen ? 'controlPrimary' : 'currentColor'} />
          </div>
        )}
      </div>
    </div>
  );
});

Select.displayName = 'Select';
